import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from '../../../components/bootstrap/Button';
import Spinner from '../../../components/bootstrap/Spinner';
import { toast } from 'react-toastify';
import useDarkMode from '../../../hooks/useDarkMode';
import CustomTextInput from '../CustomTextInput';
import authAPI from '../../API/authAPI';
import Icon from '../../../components/icon/Icon';

interface ForgotPasswordComponentProps {
	onBack: () => void;
	onTokenSent: (email: string) => void;
}

const ForgotPasswordComponent = ({ onBack, onTokenSent }: ForgotPasswordComponentProps) => {
	const { darkModeStatus } = useDarkMode();
	const [isLoading, setIsLoading] = useState(false);

	const formik = useFormik({
		initialValues: {
			email: '',
		},
		validationSchema: Yup.object().shape({
			email: Yup.string().required('Email is required').email('Invalid email format'),
		}),
		onSubmit: async (values) => {
			setIsLoading(true);
			try {
				const data = await authAPI.forgotPassword(values.email);
				if (data.success) {
					toast.success(data.message || 'Reset code sent to your email', {
						position: 'top-right',
						autoClose: 5000,
						theme: darkModeStatus ? 'dark' : 'light',
					});
					onTokenSent(values.email);
				} else {
					toast.error(data.message || 'Failed to send reset code', {
						position: 'top-right',
						autoClose: 5000,
						theme: darkModeStatus ? 'dark' : 'light',
					});
				}
			} catch (error: any) {
				const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred. Please try again.';
				toast.error(errorMessage, {
					position: 'top-right',
					autoClose: 5000,
					theme: darkModeStatus ? 'dark' : 'light',
				});
			} finally {
				setIsLoading(false);
			}
		},
	});

	return (
		<div className="forgot-password-form">
			<div className="text-center mb-4">
				<Icon icon="Lock" size="3x" color="primary" className="mb-3" />
				<h4 className="mb-2">Forgot Password?</h4>
				<p className="text-muted">
					Enter your email address and we'll send you a verification code to reset your password.
				</p>
			</div>

			<form onSubmit={formik.handleSubmit}>
				<div className="mb-3">
					<CustomTextInput
						id="email"
						placeholder="Enter your email address"
						autoComplete="email"
						value={formik.values.email}
						isTouched={formik.touched.email}
						invalidFeedback={formik.errors.email}
						isValid={formik.isValid}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
					/>
				</div>

				<div className="d-grid gap-2">
					<Button
						type="submit"
						color="primary"
						className="py-3"
						isDisable={isLoading || !formik.isValid}
					>
						{isLoading && <Spinner className="me-2" isSmall inButton isGrow />}
						Send Reset Code
					</Button>
					
					<Button
						type="button"
						color="light"
						className="py-3"
						onClick={onBack}
						isDisable={isLoading}
					>
						<Icon icon="ArrowBack" className="me-2" />
						Back to Login
					</Button>
				</div>
			</form>
		</div>
	);
};

export default ForgotPasswordComponent;